import useI18n from '@/shared/hooks/usei18n'
import type { AggregatedProjection } from '@/features/cbam-reporting/hooks/cbamLiability'
import type { CbamModifiers } from '@/shared/types/cbamModifiers'
import { Spinner } from '@/common'
import QuarterLiabilityCard from './QuarterLiabilityCard'
import { QUARTERS, type Quarter } from '@/config/cbamReportingPeriods'

type Props = {
  modifiers: CbamModifiers
  simulationYear: number
  /** Only quarter cards for these quarters are shown (quarters that have at least one good). */
  quartersWithGoods: Quarter[]
  aggregatedProjectionByQuarter: Record<string, Record<string, AggregatedProjection>> | undefined
  /** High-CSCF projections when user CSCF < 100%; used for high liability in quarter cards. */
  aggregatedProjectionByQuarterHigh?: Record<string, Record<string, AggregatedProjection>>
  isLoading: boolean
}

export default function CertificatesAndLiabilityPerQuarter({
  modifiers,
  simulationYear,
  quartersWithGoods,
  aggregatedProjectionByQuarter,
  aggregatedProjectionByQuarterHigh,
  isLoading
}: Props) {
  const { t } = useI18n()
  const yearKey = String(simulationYear)
  const quartersByYear = aggregatedProjectionByQuarter?.[yearKey]
  const quartersByYearHigh = aggregatedProjectionByQuarterHigh?.[yearKey]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  if (quartersWithGoods.length === 0) {
    return (
      <p className="text-gray-600">
        {t('cbamModifiers:certificatesAndLiabilityPerQuarter.noData')}
      </p>
    )
  }

  const quartersToShow = [...quartersWithGoods].sort(
    (quarterA, quarterB) => QUARTERS.indexOf(quarterA) - QUARTERS.indexOf(quarterB)
  )

  return (
    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
      {quartersToShow.map(quarter => {
        return (
          <QuarterLiabilityCard
            yearKey={yearKey}
            key={quarter}
            modifiers={modifiers}
            quartersByYear={quartersByYear}
            quartersByYearHigh={quartersByYearHigh}
            quarter={quarter}
          />
        )
      })}
    </div>
  )
}
